/**
 * MAP-24 shape-by: deterministic value→marker-shape assignment, the exact
 * sibling of `categoryColoring` in `./color`. The same category value maps to
 * the same shape across reloads, share links, and embeds because the assignment
 * is a pure function of the column's value SET (not the filtered view): distinct
 * values are ordered by frequency (desc) then value (asc) and handed shapes from
 * a fixed cycle in sequence — never re-cycled — with everything past the cycle
 * cap folded into a single "Other" bucket (a reserved fallback shape).
 *
 * The 8 shapes shipped with the global marker picker are the palette here. The
 * cycle is ordered for legibility so the common (2–6 value) case gets maximally
 * distinct shapes; the two teardrop pins and the plain circle sort last. `circle`
 * doubles as the Other fallback, so an overflow bucket only ever collides with
 * the 8th-most-frequent value — and only when a column has 9+ distinct values.
 */

import type { MarkerShape } from "./mapspec";

const NON_EMPTY = (v: string): boolean => v.trim() !== "";

/** The deterministic shape cycle (assignment order). Distinct-first for legibility. */
export const SHAPE_BY_CYCLE: MarkerShape[] = [
  "pin",
  "square",
  "diamond",
  "triangle",
  "star",
  "hexagon",
  "pin-solid",
  "circle",
];

/** The Other-bucket fallback shape (values past the cycle cap). */
export const SHAPE_BY_OTHER: MarkerShape = "circle";

export interface ShapeEntry {
  value: string;
  shape: MarkerShape;
  isOther?: boolean;
}

export interface ShapeByAssignment {
  /** Values that received their own cycle slot, in assignment order. */
  order: string[];
  /** value → shape for every value that renders as its own shape. */
  shapeFor: Record<string, MarkerShape>;
  /** Values folded into the Other bucket. */
  otherValues: string[];
  other: MarkerShape;
  hasOther: boolean;
  /** Legend rows (ordered slots, then a single "Other" row if needed). */
  entries: ShapeEntry[];
}

/**
 * Assign shapes to a column's values. `values` is every point's value for the
 * column (duplicates expected — frequency drives ordering). The cap is the
 * cycle length; extras fold into the Other bucket. Deterministic and pure.
 */
export function shapeAssignment(
  values: string[],
  cycle: MarkerShape[] = SHAPE_BY_CYCLE,
  other: MarkerShape = SHAPE_BY_OTHER,
): ShapeByAssignment {
  const freq = new Map<string, number>();
  for (const v of values) {
    if (!NON_EMPTY(v)) continue;
    freq.set(v, (freq.get(v) ?? 0) + 1);
  }
  const distinct = [...freq.keys()].sort((a, b) => {
    const d = freq.get(b)! - freq.get(a)!;
    return d !== 0 ? d : a < b ? -1 : a > b ? 1 : 0;
  });

  const cap = cycle.length;
  const order = distinct.slice(0, cap);
  const otherValues = distinct.slice(cap);

  const shapeFor: Record<string, MarkerShape> = {};
  order.forEach((v, i) => {
    shapeFor[v] = cycle[i]!;
  });

  const entries: ShapeEntry[] = order.map((v) => ({ value: v, shape: shapeFor[v]! }));
  const hasOther = otherValues.length > 0;
  if (hasOther) entries.push({ value: "Other", shape: other, isOther: true });

  return { order, shapeFor, otherValues, other, hasOther, entries };
}
